import { migrate } from './migrations';
import { createDefaultSave, MAX_LIST, SAVE_KEY, type SaveData } from './schema';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export class MemoryStorage implements StorageLike {
  private map = new Map<string, string>();
  getItem(key: string): string | null {
    return this.map.get(key) ?? null;
  }
  setItem(key: string, value: string): void {
    this.map.set(key, value);
  }
  removeItem(key: string): void {
    this.map.delete(key);
  }
}

export function probeLocalStorage(): StorageLike | null {
  try {
    const s = globalThis.localStorage;
    if (!s) return null;
    const probe = '__lcca_probe__';
    s.setItem(probe, '1');
    s.removeItem(probe);
    return s;
  } catch {
    return null;
  }
}

export interface SaveServiceOptions {
  key?: string;
  delayMs?: number;
  now?: () => number;
  onChange?: (data: Readonly<SaveData>) => void;
}

export class SaveService {
  readonly key: string;
  private storage: StorageLike;
  private current: SaveData;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private readonly delayMs: number;
  private readonly now: () => number;
  private readonly onChange: ((data: Readonly<SaveData>) => void) | undefined;
  persistent: boolean;
  recoveredFromCorruption = false;

  constructor(storage: StorageLike | null, options: SaveServiceOptions = {}) {
    this.key = options.key ?? SAVE_KEY;
    this.delayMs = options.delayMs ?? 500;
    this.now = options.now ?? Date.now;
    this.onChange = options.onChange;
    this.persistent = storage !== null;
    this.storage = storage ?? new MemoryStorage();
    this.current = this.load();
  }

  get data(): Readonly<SaveData> {
    return this.current;
  }

  get dirty(): boolean {
    return this.timer !== null;
  }

  update(mutate: (draft: SaveData) => void): void {
    mutate(this.current);
    this.current.prayers = this.current.prayers.slice(-MAX_LIST);
    this.current.fortunes = this.current.fortunes.slice(-MAX_LIST);
    this.current.keoResults = this.current.keoResults.slice(-MAX_LIST);
    this.schedule();
    this.onChange?.(this.current);
  }

  flush(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    this.write();
  }

  reset(): void {
    this.current = createDefaultSave(this.now());
    this.flush();
    this.onChange?.(this.current);
  }

  attachLifecycle(target: Window): () => void {
    const onHide = (): void => {
      if (this.dirty) this.flush();
    };
    const onVisibility = (): void => {
      if (target.document.visibilityState === 'hidden') onHide();
    };
    target.addEventListener('pagehide', onHide);
    target.addEventListener('beforeunload', onHide);
    target.document.addEventListener('visibilitychange', onVisibility);
    return () => {
      target.removeEventListener('pagehide', onHide);
      target.removeEventListener('beforeunload', onHide);
      target.document.removeEventListener('visibilitychange', onVisibility);
    };
  }

  private schedule(): void {
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      this.write();
    }, this.delayMs);
  }

  private write(): void {
    try {
      this.storage.setItem(this.key, JSON.stringify(this.current));
    } catch {
      this.storage = new MemoryStorage();
      this.persistent = false;
      this.storage.setItem(this.key, JSON.stringify(this.current));
    }
  }

  private load(): SaveData {
    const now = this.now();
    let raw: string | null = null;
    try {
      raw = this.storage.getItem(this.key);
    } catch {
      this.storage = new MemoryStorage();
      this.persistent = false;
    }
    if (raw === null) return createDefaultSave(now);
    try {
      return migrate(JSON.parse(raw), now);
    } catch {
      this.recoveredFromCorruption = true;
      try {
        this.storage.setItem(`${this.key}:corrupt:${now}`, raw);
      } catch {
        // Losing the backup is acceptable; losing the fresh save is not.
      }
      return createDefaultSave(now);
    }
  }
}
